import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { useSanctum } from "react-sanctum";
import { Coins } from "lucide-react";

// Components
import Alert from "../../components/alert";
import Button from "../../components/button";
import GenerateMarzipanoView from "../../components/generate-marzipano-view";
import Input from "../../components/input";
import UpIcon from "../../icons/up";

// Services
import { Api } from "../../services/Api";

// Types
import { GenerationPageProps, StyleInterface } from "../../layout/generate";

interface StyleMenuProps {
  styles: StyleInterface[];
  selectedStyle: StyleInterface | null;
  previewImage: string;
  onStyleSelect: (style: StyleInterface) => void;
  onStyleHover: (style: StyleInterface) => void;
}

const StyleMenu = ({ styles, selectedStyle, previewImage, onStyleSelect, onStyleHover }: StyleMenuProps) => {
  if (!selectedStyle) return null;

  return (
    <div className="absolute bottom-full left-0 z-10 mb-sm min-w-3xl rounded-md border border-slate-200 bg-slate-300 pb-sm">
      <div className="p-sm">
        {previewImage && (
          <img
            src={previewImage}
            alt="Style preview"
            className="w-full rounded-sm"
          />
        )}
      </div>
      <div className="max-h-3xl overflow-y-auto">
        {styles.map((style) => (
          <button
            key={style.id}
            className="block w-full px-md py-sm text-left hover:bg-slate-200"
            onMouseEnter={() => onStyleHover(style)}
            onClick={() => onStyleSelect(style)}
          >
            {style.name}
          </button>
        ))}
      </div>
    </div>
  );
};

export default function Generate() {
  // Hooks
  const { user } = useSanctum();
  const { generation, requestedGeneration, scene, setSaveGenerationPopupOpen } =
    useOutletContext<GenerationPageProps>();

  // State
  const [isStyleMenuOpen, setIsStyleMenuOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [styles, setStyles] = useState<StyleInterface[]>([]);
  const [selectedStyle, setSelectedStyle] = useState<StyleInterface | null>(null);
  const [prompt, setPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [credits, setCredits] = useState(0);

  // Handlers
  const handleStyleButtonClick = () => setIsStyleMenuOpen(!isStyleMenuOpen);
  const handleStyleOptionHover = (style: StyleInterface) => setPreviewImage(style.image_url);
  const handleStyleSelect = (style: StyleInterface) => {
    setSelectedStyle(style);
    setIsStyleMenuOpen(false);
  };

  const handleGenerate = async () => {
    if (!selectedStyle || !prompt) return;

    setIsLoading(true);
    try {
      const response = await Api("/users/content/generate", {
        method: "POST",
        data: { style_id: selectedStyle.id, positive_prompt: prompt },
      });
      setPrompt("");
      requestedGeneration(response.generation);
      setCredits(credits - 1);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === "Enter" || e.key === "NumpadEnter") && canGenerate) {
      handleGenerate();
    }
  };

  // Effects
  useEffect(() => {
    if (user?.user?.ai_credits !== undefined) {
      setCredits(user.user.ai_credits);
    }
  }, [user]);

  useEffect(() => {
    Api("/users/content/generate/styles")
      .then((response) => {
        const filteredStyles = response.filter(
          (style: StyleInterface) =>
            !style.name.includes("(Featured)") &&
            !style.name.includes("testing"),
        );
        setStyles(filteredStyles);
        setSelectedStyle(filteredStyles[0]);
        setPreviewImage(filteredStyles[0].image_url);
      })
      .catch(console.error);
  }, []);

  // Computed values
  const canGenerate =
    styles.length > 0 &&
    selectedStyle &&
    prompt &&
    prompt.length <= (selectedStyle.max_characters || 200) &&
    !isLoading &&
    credits > 0;

  const canSave =
    generation?.image_url &&
    !isLoading &&
    !generation?.error_message;

  return (
    <div className="relative flex-1">
      {/* Generation View */}
      {generation?.image_url && scene?.get("1")?.imageUrl ? (
        <GenerateMarzipanoView 
          key={scene.get("1").imageUrl} 
          className="h-full" 
          scene={scene} 
        />
      ) : (
        <div className="flex h-full items-center justify-center">
          <div className="flex flex-col items-center gap-lg text-center max-w-xl pb-2xl">
            <div className="flex flex-col items-center gap-md">
              <h1 className="text-2xl font-bold text-slate-100">AI Content Generator</h1>
            </div>
            <p className="text-slate-100">
              Create stunning 360° environments using AI. Simply enter a text description 
              of the scene you want to generate, choose a style, and our AI partner will create an 
              immersive panoramic environment.
            </p>
            <p className="text-slate-100">
              You have {credits} credit{credits !== 1 ? 's' : ''} remaining. Each generation uses 1 credit. Credits are automatically refilled on the first day of each month.
            </p>
          </div>
        </div>
      )}

      {/* Status Messages */}
      {generation && !generation.error_message && !generation.image_url && (
        <div className="absolute left-0 top-0 flex w-full justify-center p-md">
          <Alert
            text="Generating... this might take a couple of minutes."
            colour="success"
          />
        </div>
      )}

      {generation?.error_message && (
        <div className="absolute left-0 top-0 flex w-full justify-center p-md">
          <Alert text={`Error: ${generation.error_message}`} colour="danger" />
        </div>
      )}

      {/* Controls */}
      <div className="absolute bottom-0 left-0 flex w-full justify-center p-md">
        <div className="flex w-full max-w-4xl flex-col gap-sm rounded-lg bg-slate-500 bg-opacity-90 p-md backdrop-blur-md">
          <div className="md:flex-1">
            <Input
              type="text"
              id="generate"
              name="generate"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              required
              maxLength={selectedStyle?.max_characters || 200}
              placeholder="Enter a prompt..."
              disabled={isLoading}
            />
          </div>

          <div className="flex flex-col gap-sm sm:flex-row">
            <div className="flex flex-1 gap-sm">
              <div className="relative flex-1 sm:flex-none">
                <Button
                  block
                  onClick={handleStyleButtonClick}
                  disabled={!styles.length || isLoading}
                >
                  <span className="flex-1">
                    {selectedStyle?.name || "Style"}
                  </span>
                  <UpIcon />
                </Button>

                {isStyleMenuOpen && (
                  <StyleMenu
                    styles={styles}
                    selectedStyle={selectedStyle}
                    previewImage={previewImage}
                    onStyleSelect={handleStyleSelect}
                    onStyleHover={handleStyleOptionHover}
                  />
                )}
              </div>
              <Button onClick={handleGenerate} disabled={!canGenerate}>
                Generate
              </Button>
              <div className="flex items-center gap-sm rounded-md bg-slate-400 px-md text-slate-100">
                <Coins />
                <span className="text-sm">{credits}</span>
              </div>
            </div>

            <div>
              <Button
                block
                primary
                onClick={() => setSaveGenerationPopupOpen(true)}
                disabled={!canSave}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
